/* ==========================================================================
   whero-locations-page.js — /pages/locations dedicated section.

   Responsibilities:
     1. Recompute per-shop status (open / closing / closed) in America/
        Los_Angeles every 60s and apply it to the .locpage-location__status
        block. Special-hours lookup overrides regular hours on matching
        ISO dates; today's row in the hours table gets a .today class and
        any special label renders inline.
     2. Duplicate the marquee track 3x for seamless looping (pure CSS
        wouldn't produce a continuous scroll without duplication).
     3. Listen for whero:status-updated (from whero-status-pill.js) to
        paint distance labels on each card + jump link. The distance
        pill itself doubles as the closest-shop indicator (lower number
        = closer shop), so no separate badge is rendered.

   Queries scope to [data-whero-locations-page-section-id] so multiple
   instances on one page (unlikely but possible) don't collide.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-whero-locations-page-section-id]';
  var DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  function forEach(list, fn) {
    for (var i = 0; i < list.length; i++) fn(list[i], i);
  }

  function laNow() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      hour12: false,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date());
    var bag = {};
    for (var i = 0; i < parts.length; i++) bag[parts[i].type] = parts[i].value;
    var dayKey = (bag.weekday || '').toLowerCase().slice(0, 3);
    var dow = DAY_NAMES.indexOf(dayKey);
    if (dow < 0) dow = 0;
    var hour = parseInt(bag.hour, 10) % 24;
    var minute = parseInt(bag.minute, 10);
    return {
      dow: dow,
      dayKey: dayKey,
      hour: hour,
      minute: minute,
      mins: hour * 60 + minute,
      iso: bag.year + '-' + bag.month + '-' + bag.day,
    };
  }

  function hhmmToMins(hhmm) {
    if (typeof hhmm !== 'string' || hhmm.length < 4) return NaN;
    var parts = hhmm.split(':');
    if (parts.length !== 2) return NaN;
    var h = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return NaN;
    return h * 60 + m;
  }

  function formatClock(hhmm) {
    var parts = (hhmm || '').split(':');
    if (parts.length !== 2) return '';
    var h = parseInt(parts[0], 10);
    var m = parts[1];
    if (isNaN(h)) return '';
    var dh, ampm;
    if (h === 0)       { dh = 12; ampm = 'am'; }
    else if (h < 12)   { dh = h;  ampm = 'am'; }
    else if (h === 12) { dh = 12; ampm = 'pm'; }
    else               { dh = h - 12; ampm = 'pm'; }
    return m === '00' ? (dh + ampm) : (dh + ':' + m + ampm);
  }

  function formatDistance(mi) {
    if (typeof mi !== 'number' || isNaN(mi)) return '';
    return (mi < 10 ? mi.toFixed(1) : Math.round(mi)) + ' mi';
  }

  /* ---- Per-article setup ------------------------------------------------- */

  function readHoursData(article) {
    var el = article.querySelector('script[data-locpage-hours-data]');
    if (!el) return null;
    try { return JSON.parse(el.textContent || 'null'); }
    catch (_) { return null; }
  }

  function hoursForToday(data, now) {
    var special = data.special && data.special[now.iso];
    if (special) {
      return {
        open: special.open || '',
        close: special.close || '',
        closed: !!special.closed,
        label: special.label || '',
        isSpecial: true,
      };
    }
    var day = (data.regular && data.regular[now.dayKey]) || null;
    if (!day || !day.found) {
      return { open: '', close: '', closed: true, label: '', isSpecial: false };
    }
    return {
      open: day.open || '',
      close: day.close || '',
      closed: !!day.closed,
      label: '',
      isSpecial: false,
    };
  }

  function hoursForTomorrow(data, now) {
    var tomorrowKey = DAY_NAMES[(now.dow + 1) % 7];
    var day = (data.regular && data.regular[tomorrowKey]) || null;
    if (!day || !day.found || day.closed) return null;
    return { open: day.open, close: day.close };
  }

  function statusFor(data, now) {
    var today = hoursForToday(data, now);
    var openMins = hhmmToMins(today.open);
    var closeMins = hhmmToMins(today.close);
    var state, label, sub;

    if (today.closed || isNaN(openMins) || isNaN(closeMins)) {
      state = 'is-closed';
      label = 'Closed';
      sub = today.label ? today.label : '';
    } else if (now.mins < openMins) {
      state = 'is-closed';
      label = 'Closed';
      sub = 'Opens at ' + formatClock(today.open) + ' today';
    } else if (now.mins >= closeMins) {
      state = 'is-closed';
      label = 'Closed for the night';
      var tmr = hoursForTomorrow(data, now);
      sub = tmr ? 'Opens ' + formatClock(tmr.open) + ' tomorrow' : '';
    } else {
      var untilClose = closeMins - now.mins;
      if (untilClose <= 60) {
        state = 'is-closing';
        label = 'Closing soon';
        sub = 'Closes in ' + untilClose + ' min';
      } else {
        state = 'is-open';
        label = 'Open now';
        sub = 'Closes at ' + formatClock(today.close);
      }
    }

    var range = (today.closed || !today.open || !today.close)
      ? 'Closed'
      : formatClock(today.open) + '–' + formatClock(today.close);

    return {
      state: state,
      label: label,
      sub: sub,
      range: range,
      specialLabel: today.isSpecial ? today.label : '',
    };
  }

  function paintStatus(article, data, now) {
    var s = statusFor(data, now);
    var box = article.querySelector('[data-locpage-status]');
    if (box) {
      box.classList.remove('is-open', 'is-closing', 'is-closed');
      box.classList.add(s.state);
      var lbl = box.querySelector('[data-locpage-status-label]');
      var sub = box.querySelector('[data-locpage-status-sub]');
      if (lbl) lbl.textContent = s.label;
      if (sub) {
        sub.textContent = s.sub;
        if (s.sub) sub.removeAttribute('hidden');
        else sub.setAttribute('hidden', '');
      }
    }
    var rangeEl = article.querySelector('[data-locpage-today-range]');
    if (rangeEl) rangeEl.textContent = s.range;

    var table = article.querySelector('[data-locpage-hours-table]');
    if (table) {
      forEach(table.querySelectorAll('tr'), function (tr) {
        var day = parseInt(tr.getAttribute('data-day'), 10);
        tr.classList.toggle('today', day === now.dow);
        tr.classList.toggle('special', day === now.dow && !!s.specialLabel);

        if (day === now.dow) {
          var dayCell = tr.querySelector('.locpage-location__hours-day');
          if (dayCell) {
            // Remove any prior special-label span, then re-add if applicable.
            var existing = dayCell.querySelector('.special-label');
            if (existing) existing.parentNode.removeChild(existing);
            if (s.specialLabel) {
              var span = document.createElement('span');
              span.className = 'special-label';
              span.textContent = s.specialLabel;
              dayCell.appendChild(span);
            }
          }
        }
      });
    }
  }

  /* ---- Marquee: duplicate track for seamless loop ----------------------- */

  function setupMarquee(article) {
    var viewport = article.querySelector('[data-locpage-marquee]');
    if (!viewport) return;
    var track = viewport.querySelector('.locpage-location__marquee-track');
    if (!track) return;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    // Duplicate twice for a 3x track so the loop reads continuous.
    var clone1 = track.cloneNode(true);
    var clone2 = track.cloneNode(true);
    clone1.setAttribute('aria-hidden', 'true');
    clone2.setAttribute('aria-hidden', 'true');
    // Merge into a single track by moving children of clones into the original.
    while (clone1.firstChild) track.appendChild(clone1.firstChild);
    while (clone2.firstChild) track.appendChild(clone2.firstChild);
  }

  /* ---- Distance / closest-shop listener --------------------------------- */

  function applyDistances(section, detail) {
    if (!detail) return;
    var ranked = Array.isArray(detail.allShops) ? detail.allShops : [];
    var byId = {};
    for (var i = 0; i < ranked.length; i++) {
      var id = ranked[i].shop && ranked[i].shop.id;
      if (id != null) byId[String(id)] = ranked[i];
    }
    var showDistance = section.getAttribute('data-show-distance') === 'true';

    forEach(section.querySelectorAll('.locpage-location'), function (article) {
      var shopId = article.getAttribute('data-shop');
      var entry = shopId ? byId[String(shopId)] : null;

      var distEl = article.querySelector('[data-locpage-distance]');
      var distTextEl = article.querySelector('[data-locpage-distance-text]');
      if (distEl && distTextEl) {
        if (showDistance && entry && typeof entry.distanceMi === 'number' && !isNaN(entry.distanceMi)) {
          distTextEl.textContent = formatDistance(entry.distanceMi) + ' away';
          distEl.removeAttribute('hidden');
        } else {
          distEl.setAttribute('hidden', '');
        }
      }
    });

    forEach(section.querySelectorAll('[data-locpage-jump-distance]'), function (distEl) {
      var linkEl = distEl.closest('[data-shop]');
      var shopId = linkEl && linkEl.getAttribute('data-shop');
      var entry = shopId ? byId[String(shopId)] : null;
      if (showDistance && entry && typeof entry.distanceMi === 'number' && !isNaN(entry.distanceMi)) {
        distEl.textContent = formatDistance(entry.distanceMi);
        distEl.removeAttribute('hidden');
      } else {
        distEl.setAttribute('hidden', '');
      }
    });
  }

  /* ---- Boot -------------------------------------------------------------- */

  function bootSection(section) {
    var articles = section.querySelectorAll('.locpage-location');
    if (articles.length === 0) return;

    var dataBySection = [];
    forEach(articles, function (article) {
      var data = readHoursData(article);
      if (data) dataBySection.push({ article: article, data: data });
      setupMarquee(article);
    });

    function paintAll() {
      var now = laNow();
      for (var i = 0; i < dataBySection.length; i++) {
        paintStatus(dataBySection[i].article, dataBySection[i].data, now);
      }
    }

    paintAll();
    setInterval(paintAll, 60 * 1000);

    // Probe any already-resolved status-pill state (race with event timing).
    function probeAndApply() {
      try {
        if (window.WheroStatusPill && typeof window.WheroStatusPill.getState === 'function') {
          var s = window.WheroStatusPill.getState();
          if (s && s.lastRender && s.lastRender.closest) {
            applyDistances(section, {
              closestShop: s.lastRender.closest.shop,
              allShops: s.lastRender.ranked || [],
            });
          }
        }
      } catch (_) {}
    }
    probeAndApply();

    document.addEventListener('whero:status-updated', function (e) {
      applyDistances(section, e && e.detail);
    });
  }

  function boot() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    forEach(sections, bootSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
